import { useAuthenticator } from '@aws-amplify/ui-react';
import { skipToken } from '@reduxjs/toolkit/query';
import Button from 'components/actions/Button';
import RequestedValue from 'components/content/RequestedValue';
import Switch from 'components/forms/Switch';
import ActionBarItem, {
    ActionBarItemIcon,
    ActionBarItemList,
    ActionBarItemListItem,
    ActionBarItemListSeparator,
    ActionBarItemPopover,
} from 'components/navigation/ActionBarItem';
import { closeVehicleDetailsWindow } from 'hooks/utils';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGetUserAttributesQuery, useGetUserQuery, userApi } from 'services/user';
import { vehiclesV2Api } from 'services/vehiclesV2';
import { persistor } from 'store/setup';
import { getVehicleDetailsPageReference, resetAppState } from 'store/slices/appSlice';
import { getMeasurementSystem, setMeasurementSystem } from 'store/slices/userSettingsSlice';

import { systemFleetsApi, useListSystemFleetsQuery } from '../../services/systemAdmin/fleets';
import styles from './styles/AccountMenu.module.scss';

const AccountMenu = () => {
    const dispatch = useDispatch();
    const { signOut } = useAuthenticator(context => [context.user]);
    const vehicleDetailsReference = useSelector(getVehicleDetailsPageReference);
    const measurementSystem = useSelector(getMeasurementSystem);

    const { data: userAttributes } = useGetUserAttributesQuery('User');
    const userAttributesSub = userAttributes?.sub ?? '';
    const {
        data: userData,
        isLoading,
        isFetching,
    } = useGetUserQuery(userAttributesSub ? userAttributesSub : skipToken);
    const isFetchingUserData = isLoading || isFetching;

    const { data: systemFleets } = useListSystemFleetsQuery('ListSystemFleets');

    const { userRoles, userTeams, userFleets } = useMemo(() => {
        const userTeamIdSet = new Set((userData?.teams ?? []).map(team => team.id));

        const userFleetNameSet = new Set<string>();
        for (const fleet of systemFleets ?? []) {
            const fleetTeams = fleet?.teams ?? [];
            if (fleetTeams.some(team => userTeamIdSet.has(team.id))) {
                userFleetNameSet.add(fleet.name);
            }
        }

        return {
            userRoles: userData?.roles ?? [],
            userTeams: userData?.teams?.map(t => t.name) ?? [],
            userFleets: [...userFleetNameSet],
        };
    }, [userData?.roles, userData?.teams, systemFleets]);

    const logOut = useCallback(() => {
        // close vehicle details if opened programmatically by the application
        closeVehicleDetailsWindow(vehicleDetailsReference);
        signOut();
        dispatch(userApi.util.resetApiState());
        dispatch(vehiclesV2Api.util.resetApiState());
        dispatch(systemFleetsApi.util.resetApiState());
        dispatch(resetAppState());
        void persistor.purge();
    }, [dispatch, signOut, vehicleDetailsReference]);

    return (
        <ActionBarItem hidePopoverOnClick={false} popoverWidth={300}>
            <ActionBarItemIcon>
                <span className='icon rioglyph rioglyph-user' />
            </ActionBarItemIcon>
            <ActionBarItemPopover>
                <ActionBarItemList>
                    <ActionBarItemListItem>
                        <RequestedValue
                            isLoading={isFetchingUserData}
                            height='21px'
                            isMissing={(!userData?.firstName || !userData?.lastName) && !isFetchingUserData}
                        >
                            <p className='label-name'>
                                {userData?.firstName}&nbsp;{userData?.lastName}
                            </p>
                        </RequestedValue>
                        <RequestedValue
                            isLoading={isFetchingUserData}
                            isMissing={!userData?.email && !isFetchingUserData}
                        >
                            <p className={styles.AccountMenu__text}>{userData?.email}</p>
                        </RequestedValue>
                    </ActionBarItemListItem>
                    <ActionBarItemListItem>
                        <h4 className={styles.AccountMenu__label}>Role(s)</h4>
                        <RequestedValue
                            isLoading={isFetchingUserData}
                            isMissing={!userRoles?.length && !isFetchingUserData}
                        >
                            <ul>
                                {userRoles.map(userRole => (
                                    <li className={styles.AccountMenu__text} key={userRole.id}>
                                        {userRole.name}
                                    </li>
                                ))}
                            </ul>
                        </RequestedValue>
                    </ActionBarItemListItem>
                    <ActionBarItemListItem>
                        <h4 className={styles.AccountMenu__label}>Team(s)</h4>
                        <RequestedValue
                            isLoading={isFetchingUserData}
                            isMissing={!userTeams?.length && !isFetchingUserData}
                        >
                            <ul>
                                {userTeams.map(userTeam => (
                                    <li className={styles.AccountMenu__text} key={userTeam}>
                                        {userTeam}
                                    </li>
                                ))}
                            </ul>
                        </RequestedValue>
                    </ActionBarItemListItem>
                    <ActionBarItemListItem>
                        <h4 className={styles.AccountMenu__label}>Fleet(s)</h4>
                        <RequestedValue
                            isLoading={isFetchingUserData}
                            isMissing={!userFleets?.length && !isFetchingUserData}
                        >
                            <ul>
                                {userTeams.map(userTeam => (
                                    <li className={styles.AccountMenu__text} key={userTeam}>
                                        {userTeam}
                                    </li>
                                ))}
                            </ul>
                        </RequestedValue>
                    </ActionBarItemListItem>
                    <ActionBarItemListItem>
                        <h4 className={styles.AccountMenu__label}>Units &amp; date format</h4>
                        <Switch
                            checked={measurementSystem === 'us'}
                            onChange={checked => dispatch(setMeasurementSystem(checked ? 'us' : 'eu'))}
                            className={styles.AccountMenu__switch}
                            enabledText='US'
                            disabledText='EU'
                            autoMinWidth
                        />
                    </ActionBarItemListItem>
                    <ActionBarItemListSeparator />
                    <ActionBarItemListItem>
                        <Button onClick={logOut} style='muted'>
                            <span className='rioglyph rioglyph-logout' />
                            Log out
                        </Button>
                    </ActionBarItemListItem>
                </ActionBarItemList>
            </ActionBarItemPopover>
        </ActionBarItem>
    );
};

export default AccountMenu;
